import { Component, computed, inject, signal } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { Button, Alert, InputError } from 'excellence-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { ParseRequestCreatePage } from '../parse-request-create-page/parse-request-create-page';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { form, FormField, required } from '@angular/forms/signals';
import { ParseRequestsStore } from '../../stores/parse-requests.store';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { ParseRequestCreateAccordion } from '../../components/parse-request-create-accordion/parse-request-create-accordion';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-parse-request-create-summary-page',
  imports: [
    NgIcon,
    Button,
    TranslocoPipe,
    RouterLink,
    Alert,
    FormField,
    FieldErrors,
    InputError,
    ParseRequestCreateAccordion,
  ],
  templateUrl: './parse-request-create-summary-page.html',
})
export class ParseRequestCreateSummaryPage {
  private router = inject(Router);
  private parent = inject(ParseRequestCreatePage);
  protected activatedRoute = inject(ActivatedRoute);
  private parseRequestsStore = inject(ParseRequestsStore);
  private organizationsStore = inject(OrganizationsStore);

  protected state = this.parent.state;

  private isSubmitLoading = signal(false);
  protected isUpdateLoading = computed(
    () => !!this.state()?.isUpdateLoading || this.isSubmitLoading(),
  );
  protected updateError = computed(() => this.state()?.updateError ?? null);
  protected showValidationErrors = signal(false);

  private model = signal({
    dataProcessingConfirmation: false,
    contentAllowanceConfirmation: false,
  });

  protected form = form(this.model, (schemaPath) => {
    required(schemaPath.dataProcessingConfirmation);
    required(schemaPath.contentAllowanceConfirmation);
  });

  private markAsTouched() {
    this.form().markAsTouched();
    this.form.dataProcessingConfirmation().markAsTouched();
    this.form.contentAllowanceConfirmation().markAsTouched();
  }

  private saveData() {
    return this.parseRequestsStore.update(this.parent.parseRequestId(), {
      ...this.form().value(),
      summaryState: 'COMPLETE',
    });
  }

  protected async onSubmit() {
    const organizationId = this.organizationsStore.selectedId();
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    if (!this.form().valid()) {
      this.markAsTouched();
      this.showValidationErrors.set(true);
      return;
    }
    try {
      this.isSubmitLoading.set(true);

      await this.saveData();
      await this.parseRequestsStore.submit(this.parent.parseRequestId());
      this.organizationsStore.loadChildStats(organizationId); // fire and forget, let UI handle loading/error

      await new Promise((resolve) =>
        setTimeout(resolve, environment.delays.parseRequestSuccessPageMs),
      );
      this.isSubmitLoading.set(false);
      this.router.navigate(['../success'], {
        relativeTo: this.activatedRoute,
      });
    } catch {
      this.isSubmitLoading.set(false);
    }
  }
}
